import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Trash2, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api, apiErrorMessage } from "@/lib/api";
import { fmtEUR, fmtUSD, PARTNER_LABELS } from "@/pages/motor/shared";

export const CotizacionesList = () => {
  const qc = useQueryClient();
  const quotesQ = useQuery({ queryKey: ["quotes"], queryFn: async () => (await api.get("/quotes")).data });
  const quotes = quotesQ.data?.quotes || [];

  const recalcMut = useMutation({
    mutationFn: (id) => api.post(`/quotes/${id}/recalc?repin_active=true`),
    onSuccess: () => { toast.success("Recalculada con la fórmula activa"); qc.invalidateQueries({ queryKey: ["quotes"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/quotes/${id}`),
    onSuccess: () => { toast.success("Cotización eliminada"); qc.invalidateQueries({ queryKey: ["quotes"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (quotesQ.isLoading) {
    return <div className="space-y-2" data-testid="quotes-loading">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  }

  if (quotes.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 p-12 text-center" data-testid="quotes-empty">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground"><FileSpreadsheet className="h-6 w-6" /></div>
          <div>
            <p className="font-semibold">Aún no hay cotizaciones</p>
            <p className="text-sm text-muted-foreground">Crea una desde la pestaña “Calculadora” y guárdala para validar contra viajes reales de 2026.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table data-testid="quotes-table">
            <TableHeader>
              <TableRow>
                <TableHead>Viaje</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead className="text-right">PVP €</TableHead>
                <TableHead className="text-right">PVP $</TableHead>
                <TableHead className="text-right">Esperado €</TableHead>
                <TableHead className="text-right">Dif.</TableHead>
                <TableHead className="text-center">Validación</TableHead>
                <TableHead className="text-center">Versión</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map((q) => {
                const t = q.result?.totals || {};
                const v = q.validation;
                return (
                  <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                    <TableCell className="font-medium">{q.name}{q.trip_year ? <span className="ml-1 text-xs text-muted-foreground">· {q.trip_year}</span> : null}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-[10px]">{PARTNER_LABELS[q.partner] || q.partner}</Badge></TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{fmtEUR(t.pvp_eur)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{fmtUSD(t.pvp_usd)}</TableCell>
                    <TableCell className="text-right tabular-nums">{v ? fmtEUR(v.expected_pvp_eur) : "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{v ? `${v.diff_eur > 0 ? "+" : ""}${fmtEUR(v.diff_eur)}` : "—"}</TableCell>
                    <TableCell className="text-center">
                      {v
                        ? (v.match
                          ? <Badge className="border-0 bg-success/15 text-[10px] text-success"><CheckCircle2 className="mr-1 h-3 w-3" />Coincide</Badge>
                          : <Badge className="border-0 bg-warning/15 text-[10px] text-warning"><AlertTriangle className="mr-1 h-3 w-3" />{v.diff_pct > 0 ? "+" : ""}{v.diff_pct}%</Badge>)
                        : <span className="text-xs text-muted-foreground">sin esperado</span>}
                    </TableCell>
                    <TableCell className="text-center"><Badge variant="outline" className="text-[10px]">v{q.formula_version}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => recalcMut.mutate(q.id)} title="Recalcular con fórmula activa" data-testid={`recalc-${q.id}`} aria-label="Recalcular"><RefreshCw className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => delMut.mutate(q.id)} title="Eliminar" data-testid={`delete-${q.id}`} aria-label="Eliminar"><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
